import type { IRatingMatrixFrameworkGradeScore } from '__generated__';
import { middleColor } from 'utils/colors';

export const GRADE_COLOR_BEST = '#1F8B57';
export const GRADE_COLOR_WORST = '#B23B3B';
export const GRADE_COLOR_OUT_OF_SCOPE = '#9E9E9E';

export function buildGradeScoreTemplateFilter(templateId: number): string {
  return `frameworkTemplateID==${templateId}`;
}

export function sortGradesByNumericScore(
  grades: IRatingMatrixFrameworkGradeScore[],
): IRatingMatrixFrameworkGradeScore[] {
  return [...grades].sort((left, right) => left.numericScore - right.numericScore);
}

/** Template scale order from GetFullTemplate (best → worst). Prefer over numeric sort for qualitative lists. */
export function sortGradesBySortOrder(
  grades: IRatingMatrixFrameworkGradeScore[],
): IRatingMatrixFrameworkGradeScore[] {
  return [...grades].sort((left, right) => {
    if (left.sortOrder !== right.sortOrder) {
      return left.sortOrder - right.sortOrder;
    }
    return left.numericScore - right.numericScore;
  });
}

export type ScoreRangeDraft = {
  scoreRangeMin: number | null | undefined;
  scoreRangeMax: number | null | undefined;
};

export function normalizeNumericScore(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

export function normalizeScoreRangeBound(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }

  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeScoreRangeDraft(range: ScoreRangeDraft): ScoreRangeDraft {
  return {
    scoreRangeMin: normalizeScoreRangeBound(range.scoreRangeMin),
    scoreRangeMax: normalizeScoreRangeBound(range.scoreRangeMax),
  };
}

export function isDraftScoreRangeValid(range: ScoreRangeDraft): boolean {
  const { scoreRangeMin, scoreRangeMax } = normalizeScoreRangeDraft(range);

  if (scoreRangeMin == null || scoreRangeMax == null) {
    return false;
  }

  return scoreRangeMax > scoreRangeMin;
}

export function inferScoreRangeFromGrades(
  grades: IRatingMatrixFrameworkGradeScore[],
): ScoreRangeDraft | null {
  const sorted = sortGradesByNumericScore(grades);

  if (sorted.length < 2) {
    return null;
  }

  if (sorted.length >= 4) {
    return {
      scoreRangeMin: normalizeNumericScore(sorted[1].numericScore),
      scoreRangeMax: normalizeNumericScore(sorted[sorted.length - 2].numericScore),
    };
  }

  return {
    scoreRangeMin: normalizeNumericScore(sorted[0].numericScore),
    scoreRangeMax: normalizeNumericScore(sorted[sorted.length - 1].numericScore),
  };
}

export function resolveScoreRange(
  draft: ScoreRangeDraft,
  server?: ScoreRangeDraft,
  grades: IRatingMatrixFrameworkGradeScore[] = [],
): ScoreRangeDraft {
  const draftRange = normalizeScoreRangeDraft(draft);
  if (isDraftScoreRangeValid(draftRange)) {
    return draftRange;
  }

  const serverRange = server ? normalizeScoreRangeDraft(server) : { scoreRangeMin: null, scoreRangeMax: null };
  if (isDraftScoreRangeValid(serverRange)) {
    return serverRange;
  }

  const inferred = inferScoreRangeFromGrades(grades);
  if (inferred && isDraftScoreRangeValid(inferred)) {
    return inferred;
  }

  return draftRange;
}

export type EffectiveScoreRange = {
  min: number;
  max: number;
  hasDraftRange: boolean;
};

export function getEffectiveScoreRange(
  grades: IRatingMatrixFrameworkGradeScore[],
  resolvedRange: ScoreRangeDraft,
): EffectiveScoreRange {
  const normalized = normalizeScoreRangeDraft(resolvedRange);
  const hasResolvedRange = isDraftScoreRangeValid(normalized);

  if (hasResolvedRange) {
    return {
      min: normalized.scoreRangeMin as number,
      max: normalized.scoreRangeMax as number,
      hasDraftRange: true,
    };
  }

  if (!grades.length) {
    return { min: 0, max: 1, hasDraftRange: false };
  }

  const scores = grades
    .map((grade) => normalizeNumericScore(grade.numericScore))
    .filter((score) => Number.isFinite(score));

  if (!scores.length) {
    return { min: 0, max: 1, hasDraftRange: false };
  }

  const min = Math.min(...scores);
  const max = Math.max(...scores);

  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return { min: 0, max: 1, hasDraftRange: false };
  }

  return { min, max, hasDraftRange: false };
}

export function isGradeOutOfScope(
  grade: IRatingMatrixFrameworkGradeScore,
  resolvedRange: ScoreRangeDraft,
): boolean {
  const normalized = normalizeScoreRangeDraft(resolvedRange);

  if (!isDraftScoreRangeValid(normalized)) {
    return false;
  }

  const score = normalizeNumericScore(grade.numericScore);
  const min = normalized.scoreRangeMin as number;
  const max = normalized.scoreRangeMax as number;

  if (!Number.isFinite(score)) {
    return true;
  }

  return score < min || score > max;
}

export function getGradeScoreRatio(
  score: number,
  min: number,
  max: number,
): number {
  if (max <= min) {
    return 0;
  }

  return Math.min(1, Math.max(0, (score - min) / (max - min)));
}

export function getGradeScoreColor(
  score: number,
  min: number | null | undefined,
  max: number | null | undefined,
  outOfScope = false,
): string {
  if (outOfScope || min == null || max == null || Number.isNaN(min) || Number.isNaN(max)) {
    return GRADE_COLOR_OUT_OF_SCOPE;
  }

  return middleColor(GRADE_COLOR_BEST, GRADE_COLOR_WORST, getGradeScoreRatio(score, min, max));
}

export type GradeSortOrderPatch = {
  id: number;
  sortOrder: number;
};

export function getNextGradeSortOrder(grades: IRatingMatrixFrameworkGradeScore[]): number {
  if (!grades.length) {
    return 1;
  }

  return Math.max(...grades.map((grade) => grade.sortOrder)) + 1;
}

/** After drag reorder: assign contiguous 1..n sortOrder; patch only rows that changed. */
export function getGradeSortOrderPatches(
  orderedGrades: IRatingMatrixFrameworkGradeScore[],
  startIndex: number,
  endIndex: number,
): GradeSortOrderPatch[] {
  if (startIndex === endIndex) {
    return [];
  }

  const copyData = [...orderedGrades];
  copyData.splice(endIndex, 0, ...copyData.splice(startIndex, 1));

  const patches: GradeSortOrderPatch[] = [];

  copyData.forEach((grade, index) => {
    const newSortOrder = index + 1;
    if (grade.sortOrder !== newSortOrder) {
      patches.push({ id: grade.id, sortOrder: newSortOrder });
    }
  });

  return patches;
}

/** Gap index 0..length → splice target index after removing dragged row. */
export function computeGradeReorderTargetIndex(
  fromIndex: number,
  insertIndex: number,
): number {
  let toIndex = insertIndex;
  if (fromIndex < insertIndex) {
    toIndex = insertIndex - 1;
  }
  return toIndex;
}

export function isNoopGradeInsert(fromIndex: number, insertIndex: number): boolean {
  return computeGradeReorderTargetIndex(fromIndex, insertIndex) === fromIndex;
}

export function reorderGradesLocally(
  grades: IRatingMatrixFrameworkGradeScore[],
  fromIndex: number,
  insertIndex: number,
): IRatingMatrixFrameworkGradeScore[] {
  const toIndex = computeGradeReorderTargetIndex(fromIndex, insertIndex);
  if (toIndex === fromIndex) {
    return grades;
  }

  const copyData = [...grades];
  copyData.splice(toIndex, 0, ...copyData.splice(fromIndex, 1));
  return copyData;
}

export function buildNotchValues(
  maxNotchAdjustment: number | null | undefined,
  notchStep: number | null | undefined,
): number[] {
  const max = maxNotchAdjustment ?? 0;
  const step = notchStep ?? 0.25;

  if (max <= 0 || step <= 0) {
    return [0];
  }

  const stepsCount = Math.round(max / step);
  const values: number[] = [];

  for (let i = stepsCount; i >= -stepsCount; i -= 1) {
    values.push(Number((i * step).toFixed(4)));
  }

  return values;
}

export function isNumericScoreTaken(
  grades: IRatingMatrixFrameworkGradeScore[],
  numericScore: number,
  excludeId?: number,
): boolean {
  return grades.some(
    (grade) => grade.id !== excludeId && grade.numericScore === numericScore,
  );
}
